import React, { useEffect, useState, useRef, useCallback } from 'react';
import AdSideBar from "../ad/adSideBar";
import Loader from "../Loader/loader";
import AllGames from "../../data/gameData.json";
import AllCategories from "../../data/category.json";
import slugify from "../../utils/slugify";
import { Helmet } from 'react-helmet-async';
import LineSeparator from "../bgMadness/line";
import CardGrid from "../gamecard/cardGrid";
import Fuse from "fuse.js";
import { Button } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import BgMadness from "../bgMadness/bgMadness";

const GAMES_PER_PAGE = 24;

const Search = () => {
  const [allGames, setAllGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filteredGames, setFilteredGames] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [category, selectedCategory] = useState("");
  const [inputValue, setInputValue] = useState("");
  const observerRef = useRef();
  const location = useLocation();

  const fuse = new Fuse(AllGames.data, {
    keys: ["title"],
    threshold: 0.3,
  });

  const triggerSearch = (term) => {
    setLoading(true);
    let results = fuse.search(term || "", { limit: 500 }).map(r => r.item);
    if (category && category != "ALL") {
      results = results.filter(r => r.category === category);
    }
    setFilteredGames(results);
    setAllGames(results.slice(0, GAMES_PER_PAGE));
    setHasMore(results.length > GAMES_PER_PAGE);
    setPage(1);
    setLoading(false);
  };

  const handleSearch = () => {
    setSearchTerm(inputValue);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    const queryParams = new URLSearchParams(location.search);
    const initialTerm = queryParams.get("s") || "";
    setInputValue(initialTerm);
    setSearchTerm(initialTerm);
  }, [location.search]);

  useEffect(() => {
    if (searchTerm !== "") {
      triggerSearch(searchTerm);
    } else {
      setAllGames([]);
      setFilteredGames([]);
      setPage(1);
      setHasMore(false);
      setLoading(false);
    }
  }, [searchTerm, category]);


  useEffect(() => {
    if (searchTerm !== "") {
      triggerSearch(searchTerm);
    }
  }, [category]);

  const loadMoreGames = () => {
    const startIndex = page * GAMES_PER_PAGE;
    const newItems = filteredGames.slice(startIndex, startIndex + GAMES_PER_PAGE);
    setAllGames(prev => [...prev, ...newItems]);
    setHasMore(startIndex + GAMES_PER_PAGE < filteredGames.length);
    setPage(prev => prev + 1);
  };

  const lastElementRef = useCallback((node) => {
    if (loading || !hasMore) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        loadMoreGames();
      }
    });
    if (node) observerRef.current.observe(node);
  }, [loading, hasMore, filteredGames, page]);

  return (
    <div className="relative min-h-screen bg-[#0d0d1a] text-[#00F0FF] px-4 sm:px-6 lg:px-10 py-8 overflow-hidden">
      <Helmet>
        <title>Search Games | BrowserArena</title>
        <meta name="description" content="Search and filter hundreds of free browser games by category, tags, or titles." />
        <link rel="canonical" href="https://browserarena.com/search" />
      </Helmet>

      {/* Background Lights */}
      <BgMadness/>

      <div className="max-w-4xl mx-auto mb-6">
        <div className="flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="Search games..."
            className="flex-grow px-4 py-2 rounded-md border border-[#00F0FF] bg-[#1a1a2e] placeholder-[#777] focus:ring-[#FF007F] focus:outline-none"
          />
          <Button onClick={handleSearch} className="bg-[#00F0FF] text-[#121212] font-semibold px-4 rounded-md hover:bg-[#FF007F] transition">
            Search
          </Button>
        </div>
      </div>

      {/* Category Filter */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-8 max-w-5xl mx-auto">
        {AllCategories.map((cat, index) => (
          <Button
            key={index}
            onClick={() => selectedCategory(cat.title)}
            className={`text-sm text-center px-3 py-2 rounded border ${category === cat.title
              ? "bg-[#FF007F] text-white border-[#FF007F]"
              : "bg-[#1a1a2e] border-[#00F0FF] text-[#00F0FF] hover:border-[#FF007F]"
              } transition`}
          >
            {cat.title}
          </Button>
        ))}
      </div>
      <LineSeparator />
      {/* Grid Section */}
      {allGames.length > 0 || searchTerm =="" ?
        <CardGrid allGames={allGames} />
        :
        <div className="mt-2 sm:mt-3 text-xs sm:text-sm md:text-base text-[#00F0FF]/80 max-w-2xl mx-auto px-2 sm:px-4">
          🚫 Looks like you’ve ventured into uncharted territory. No games here… yet!
        </div>
      }

      {/* Loader */}
      {hasMore && (
        <div ref={lastElementRef} className="flex justify-center mt-10">
          <Loader />
        </div>
      )}
    </div>
  );
};

export default Search;
